import { useRef } from 'react';
import { css } from '@linaria/core';
import { Box } from '@cbhq/cds-web/layout';

import { variantGradient } from ':cloud/widgets/buildQuest/assets/colors';
import { hexagonPath } from ':cloud/widgets/buildQuest/assets/hexagon';
import { SVG_PROPS, TILE_HEIGHT, TILE_WIDTH } from ':cloud/widgets/buildQuest/constants';
import { PointsChip } from ':cloud/widgets/buildQuest/PointsChip';
import { TileProps } from ':cloud/widgets/buildQuest/types';
import { usePointsChip } from ':cloud/widgets/buildQuest/usePointsChip';

import { ReactComponent as HexIcon } from './assets/completed.svg';
import { ReactComponent as StarIcon } from './assets/star.svg';

// center an element within its parent.
function centerElement(elementWidth: number) {
  return `calc(50% - ${elementWidth / 2}px)`;
}

type CompletedTileProps = {
  centerIcon?: React.ReactElement;
  centerIconWidth?: number;
} & Pick<TileProps, 'questionId' | 'pointValue' | 'variant' | 'onPress'>;

export function CompletedTile({
  questionId,
  pointValue,
  variant,
  centerIcon = <StarIcon />,
  centerIconWidth = 80,
  onPress,
}: CompletedTileProps): React.ReactElement {
  const pointsChipRef = useRef<HTMLDivElement | null>(null);
  const { pointsChipCoordinates } = usePointsChip(pointsChipRef);

  const hexIconWidth = 44;

  const centerBlurWidth = 100;
  const centerBlur = css`
    backdrop-filter: blur(14px);
    height: ${centerBlurWidth}px;
    width: ${centerBlurWidth}px;
    border-radius: 100%;
    mask-image: radial-gradient(circle, rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 0) 75%);
  `;

  return (
    <Box
      position="relative"
      onClick={() => {
        if (onPress) {
          onPress(questionId);
        }
      }}
    >
      <svg {...SVG_PROPS}>
        <defs>
          <clipPath id="completedClip">
            <path d={hexagonPath} />
          </clipPath>
        </defs>

        {/* we have to do it this way because SVGs don't support conical gradients */}
        <foreignObject width={TILE_WIDTH} height={TILE_HEIGHT} clipPath="url(#completedClip)">
          <div className={variantGradient[variant]} style={{ height: `${TILE_HEIGHT}px` }} />
        </foreignObject>
      </svg>

      {/* center blur */}
      <Box
        position="absolute"
        top={centerElement(centerBlurWidth)}
        left={centerElement(centerBlurWidth)}
      >
        <div className={centerBlur} />
      </Box>

      {/* points chip */}
      <Box
        position="absolute"
        bottom={24} // ngl I eyeballed this
        left={pointsChipCoordinates.x}
        ref={pointsChipRef}
        key="points-chip"
      >
        <PointsChip points={pointValue} />
      </Box>

      {/* center icon */}
      <Box
        position="absolute"
        top={centerElement(centerIconWidth)}
        left={centerElement(centerIconWidth)}
      >
        {centerIcon}
      </Box>

      {/* "completed" checkmark */}
      <Box position="absolute" top={16} left={centerElement(hexIconWidth)}>
        <HexIcon width={hexIconWidth} />
      </Box>
    </Box>
  );
}
